/**
 * `ToolCallMessage` — single inline status row for one tool invocation.
 *
 * Ported from Taqat AI store
 * (`apps/client/src/components/chat/messages/tool-call-message/index.tsx`).
 * The visual contract is "icon + muted text in a row" — no card chrome,
 * no expand button. Tool state maps to icon:
 *
 *   - loading    → spinning blue Loader2
 *   - incomplete → amber AlertCircle  ("Search invoices: incomplete")
 *   - error      → red XCircle        ("Search invoices: failed")
 *   - success    → green CheckCircle  + tool-specific icon on the right
 *
 * Status copy is chosen via this priority chain (matches Taqat):
 *   1. `input.completedText` once the tool is done
 *   2. `input.statusText` while running (model-written status)
 *   3. `TOOL_CONFIGS[name].getOutputDetail(output, input)` once done
 *   4. `TOOL_CONFIGS[name].text` + extracted detail while running
 *   5. humanised tool name + heuristic detail extractor (Composio / MCP)
 *
 * Accepts both v5 (`type: "tool-${name}"`, `state`, `input`, `output`)
 * and persisted v4 (`type: "tool-invocation"`, `toolName`, `args`,
 * `result`) shapes — Mastra's memory layer round-trips messages in
 * the v4 shape unless we run them through MessageList.aiV5.ui().
 */
import { AlertCircleIcon, CheckCircle2Icon, Loader2Icon, XCircleIcon } from "lucide-react";
import {
  GENERIC_TOOL_ICON,
  extractToolDetail,
  formatToolName,
  resolveToolConfig,
} from "./tool-config.ts";

interface ToolPartLike {
  type: string;
  state?: string | undefined;
  toolName?: string | undefined;
  toolCallId?: string | undefined;
  input?: Record<string, unknown> | undefined;
  args?: Record<string, unknown> | undefined;
  output?: Record<string, unknown> | string | null | undefined;
  result?: Record<string, unknown> | string | null | undefined;
  errorText?: string | undefined;
}

interface ToolCallMessageProps {
  part: ToolPartLike;
  isMessageStreaming?: boolean;
}

export function ToolCallMessage({ part, isMessageStreaming = false }: ToolCallMessageProps) {
  const toolName = resolveToolName(part);
  const state = part.state ?? "output-available";
  const input = (part.input ?? part.args ?? {}) as Record<string, unknown>;
  const output = (part.output ?? part.result ?? null) as Record<string, unknown> | string | null;

  // Mirror Taqat: tools we never want to render in the transcript.
  if (toolName === "done" || toolName === "finish_and_respond") return null;

  // A tool is "loading" only while the parent message is still
  // streaming AND the tool hasn't reached a terminal state. Persisted
  // partial tool calls (input-available with the parent message no
  // longer streaming) are shown as "incomplete" instead — never spin
  // forever after a reload.
  const isTerminal = state === "output-available" || state === "output-error" || state === "result";
  const isIncomplete = state === "input-available" && !isMessageStreaming;
  const isError = state === "output-error" || isOutputError(output);
  const isLoading = !isTerminal && !isIncomplete;

  // Model-written status text always wins when present — it's the
  // best summary of what the tool just did.
  const lmStatusText =
    !isLoading && typeof input.completedText === "string"
      ? (input.completedText as string)
      : typeof input.statusText === "string"
        ? (input.statusText as string)
        : null;

  const config = resolveToolConfig(toolName);

  let inlineText: string;
  if (isIncomplete) {
    const detail = config?.getDetail?.(input) ?? extractToolDetail(input);
    inlineText = `${config?.label ?? formatToolName(toolName)}${detail ? `: ${detail}` : ""} (incomplete)`;
  } else if (lmStatusText) {
    inlineText = lmStatusText;
  } else if (config) {
    const outputDetail = !isLoading ? config.getOutputDetail?.(output, input) : undefined;
    const inputDetail = config.getDetail?.(input);
    if (outputDetail) {
      inlineText = outputDetail;
    } else if (config.text && inputDetail) {
      inlineText = `${config.text}: ${inputDetail}`;
    } else if (config.text) {
      inlineText = config.text;
    } else if (inputDetail) {
      inlineText = inputDetail;
    } else {
      inlineText = config.label ?? formatToolName(toolName);
    }
  } else {
    const detail = extractToolDetail(input);
    const label = formatToolName(toolName);
    inlineText = detail ? `${label}: ${detail}` : isLoading ? `${label}…` : label;
  }

  if (isError && !lmStatusText && !isIncomplete) {
    inlineText = part.errorText ?? `${config?.label ?? formatToolName(toolName)}: failed`;
  }

  const RightIcon = config?.icon ?? GENERIC_TOOL_ICON;

  return (
    <div
      id={part.toolCallId}
      // No my-* here: parent AIMessage owns spacing via gap-4. Adding
      // local margin compounds with the gap and made tool rows feel
      // unevenly spaced next to text and reasoning siblings.
      className="flex items-center gap-1.5 text-sm text-muted"
    >
      {isLoading ? (
        <Loader2Icon className="h-3.5 w-3.5 shrink-0 animate-spin text-sky-600" aria-hidden />
      ) : isIncomplete ? (
        <AlertCircleIcon className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
      ) : isError ? (
        <XCircleIcon className="h-3.5 w-3.5 shrink-0 text-bad" aria-hidden />
      ) : (
        <CheckCircle2Icon className="h-3.5 w-3.5 shrink-0 text-good" aria-hidden />
      )}

      <span className="min-w-0 truncate">{inlineText}</span>

      {!isLoading && !isError && !isIncomplete ? (
        <RightIcon className="h-3.5 w-3.5 shrink-0 text-muted/60" aria-hidden />
      ) : null}
    </div>
  );
}

function resolveToolName(part: ToolPartLike): string {
  if (typeof part.type === "string" && part.type.startsWith("tool-")) {
    return part.type.slice("tool-".length);
  }
  return part.toolName ?? "";
}

function isOutputError(output: Record<string, unknown> | string | null): boolean {
  if (output === null || output === undefined) return false;
  if (typeof output === "string") return /^error|fail/i.test(output);
  if (typeof output.success === "boolean") return !output.success;
  if (output.error !== undefined && output.error !== null) return true;
  return false;
}
